/**
 * Gated single-name inline rename: permission/schema gate + the `useInlineEdit`
 * lifecycle wired to `saveNameRename`. Extracted from `InlineCardTitle.tsx` so
 * the pipeline card title and the record header title share one
 * implementation.
 *
 * PERMISSION + SCHEMA GATE (fail-closed): `canRename` is only true when the
 * entity type is writable (`!readOnly`), the viewer holds
 * `ahoy_property_updated` on it, the `ahoy_name` schema is present/editable,
 * and the display name isn't composed from separate fields (e.g.
 * `ahoy_first_name`/`ahoy_last_name` types resolve their display name from
 * those, not `ahoy_name` — writing `ahoy_name` there would save successfully
 * and change nothing visible).
 *
 * Does NOT expose dnd-aware click/keydown handlers (`handleTitleClick` /
 * `handleTitleKeyDown`) — those wrap `startEdit()` with surface-specific
 * containment (e.g. dnd-kit drag suppression on the pipeline card) and stay
 * in the calling component.
 */

import type React from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { EditorKindDescriptor } from '@/lib/crm/inline-edit/editorKinds';
import {
  type InlineEditStatus,
  useInlineEdit,
} from '@/lib/crm/inline-edit/useInlineEdit';
import {
  hasPropertyEditor,
  isPropertyEditable,
} from '@/lib/crm/utils/propertyEditability';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { saveNameRename } from './saveNameRename';

/** Descriptor for the name-rename editor — bare single-line text input,
 *  same shape as `PipelineColumn`'s stage-rename `BARE_TEXT_DESCRIPTOR`. */
export const NAME_DESCRIPTOR: EditorKindDescriptor = {
  kind: 'bare-text',
  commitTrigger: 'enter-or-blur',
  ownsEnterKey: false,
  autoOpenPicker: false,
  defaultOpen: false,
  overlayLayout: 'row',
};

export interface UseInlineNameRenameInput {
  /** The entity being renamed. */
  object: CrmObject;
  /** `ahoy_name` property schema for this entity type. Undefined (schema
   *  still loading) fails closed to read-only. */
  nameSchema: PropertyTypeSchema | undefined;
  /** True when this entity type's display name is composed from separate
   *  fields (e.g. `ahoy_first_name`/`ahoy_last_name`) rather than resolving
   *  directly from `ahoy_name` — forces read-only. */
  isComposedName: boolean;
  /** True when the entity type itself is read-only — forces read-only
   *  regardless of schema/permission. */
  readOnly: boolean;
}

export interface UseInlineNameRenameResult {
  canRename: boolean;
  isEditing: boolean;
  status: InlineEditStatus;
  draft: unknown;
  errorMessage: string | null;
  announcement: string;
  startEdit: () => void;
  cancelEdit: () => void;
  setDraft: (value: unknown) => void;
  handleInputKeyDown: (e: KeyboardEvent) => void;
  handleBlur: (e: FocusEvent<HTMLDivElement>) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLElement | null>;
  nameSchema: PropertyTypeSchema | undefined;
}

export function useInlineNameRename({
  object,
  nameSchema,
  isComposedName,
  readOnly,
}: UseInlineNameRenameInput): UseInlineNameRenameResult {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();
  const orgId = currentOrg?.id;
  const userId = user?.id;

  const entityType = object.entity_type_name;

  const isSchemaEditable =
    !!nameSchema &&
    isPropertyEditable(nameSchema) &&
    hasPropertyEditor(nameSchema.field_type_name);

  const canRename =
    !readOnly &&
    !isComposedName &&
    isSchemaEditable &&
    hasPermission(entityType, 'ahoy_property_updated');

  const nameValue =
    getPropertyValue<string>(object.properties?.ahoy_name) ?? '';

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Deliberately NOT stabilizing `object` out of this dep list: the live
  // `getObjectSeq` read inside `saveNameRename` makes a frozen `object` safe
  // to capture, and `useInlineEdit` freezes `onSave` for the duration of an
  // edit (see its `onSaveRef` sync effect) — so the closure that actually
  // runs at commit time was captured when the edit started, not re-created
  // mid-edit.
  const onSaveRename = useCallback(
    async (value: unknown) => {
      const trimmed = (value as string).trim();
      if (!trimmed) {
        throw new Error('Name cannot be empty.');
      }
      if (!orgId || userId == null) {
        throw new Error('Could not save. Try again.');
      }
      await saveNameRename({
        object,
        orgId,
        userId,
        wireProperties: { ahoy_name: trimmed },
        optimisticProperties: {
          ahoy_name: trimmed,
          ahoy_display_name: trimmed,
        },
      });
    },
    [object, orgId, userId]
  );

  const {
    isEditing,
    status,
    draft,
    errorMessage,
    announcement,
    startEdit,
    cancelEdit,
    setDraft,
    handleKeyDown,
    handleBlur: inlineEditHandleBlur,
  } = useInlineEdit({
    descriptor: NAME_DESCRIPTOR,
    initialValue: nameValue,
    onSave: onSaveRename,
    containerRef,
    triggerRef,
  });

  // Blur on empty: cancel (no error-state lock-in); blur on non-empty: commit.
  const handleBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if ((draft as string).trim().length === 0) {
        cancelEdit();
        return;
      }
      inlineEditHandleBlur(e);
    },
    [draft, cancelEdit, inlineEditHandleBlur]
  );

  return useMemo(
    () => ({
      canRename,
      isEditing,
      status,
      draft,
      errorMessage,
      announcement,
      startEdit,
      cancelEdit,
      setDraft,
      handleInputKeyDown: handleKeyDown,
      handleBlur,
      containerRef,
      triggerRef,
      nameSchema,
    }),
    [
      canRename,
      isEditing,
      status,
      draft,
      errorMessage,
      announcement,
      startEdit,
      cancelEdit,
      setDraft,
      handleKeyDown,
      handleBlur,
      nameSchema,
    ]
  );
}
